import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  GetSupplyOrderByPoNumber,
  SaveSupplyOrder,
  UpdateSupplyOrder,
  GetProducts,
} from "../../../services/InventoryService";
import { GetVendors } from "../../../services/GeneralMaintenanceService";
import {
  supplyOrderStatusList,
  supplyOrderPriorityList,
  supplyOrderTypeList,
  inventoryOrderLevelList,
} from "../../common/statusLists";
import useInventoryLocationList from "../../common/useInventoryLocationList";
import CommonLoader from "../../common/CommonLoader";
import SupplyOrderAttachmentsTable from "./SupplyOrderAttachmentsTable";
import SupplyOrderHistoryTable from "./SupplyOrderHistoryTable";
import ReceiveSupplyOrderDialog from "./ReceiveSupplyOrderDialog";

const AddEditSupplyOrder = () => {
  const { poNumber: poNumberParam } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(poNumberParam);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [vendorRecordId, setVendorRecordId] = useState("");
  const [status, setStatus] = useState("Pending");
  const [priority, setPriority] = useState("Standard");
  const [orderType, setOrderType] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [mustShipByDate, setMustShipByDate] = useState("");
  const [receivedDate, setReceivedDate] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState("");
  const [shippingCost, setShippingCost] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  const [amountPaid, setAmountPaid] = useState(0);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [receiveOrder, setReceiveOrder] = useState(null);
  // ENB-584: Inventory Locations (Warehouse > Rack > Shelf) as full paths; saved locations no longer active are kept.
  const locationOptions = useInventoryLocationList(
    items.map((item) => item.receiveLocationName)
  );
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchLookups();
    if (isEdit) fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poNumberParam]);

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    // Local yyyy-MM-dd; toISOString would shift the day back in UTC+ zones and save it that way.
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}`;
  };

  const fetchLookups = async () => {
    try {
      const [vendorResponse, productResponse] = await Promise.all([
        GetVendors(),
        GetProducts(),
      ]);
      setVendors(vendorResponse?.results ?? []);
      setProducts(productResponse?.results ?? []);
    } catch {
      NotificationManager.error("Error loading vendors and products");
    }
  };

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await GetSupplyOrderByPoNumber(poNumberParam);
      const o = response?.results ?? response;
      if (!o) return;
      populateFields(o);
    } catch {
      NotificationManager.error("Error loading supply order");
    } finally {
      setLoading(false);
    }
  };

  const populateFields = (o) => {
    setVendorRecordId(o.vendorRecordId ?? "");
    setStatus(o.status || "Pending");
    setPriority(o.priority || "Standard");
    setOrderType(o.orderType || "");
    setOrderDate(formatDate(o.orderDate));
    setExpectedDate(formatDate(o.expectedDate));
    setMustShipByDate(formatDate(o.mustShipByDate));
    setReceivedDate(o.receivedDate ?? null);
    setTrackingNumber(o.trackingNumber || "");
    setCarrier(o.carrier || "");
    setVendorInvoiceNumber(o.vendorInvoiceNumber || "");
    setShippingCost(o.shippingCost ?? 0);
    setDepositAmount(o.depositAmount ?? 0);
    setAmountPaid(o.amountPaid ?? 0);
    setNotes(o.notes || "");
    setItems(o.items || []);
    setHistory(o.history || []);
  };

  // Receive works off the saved order, so load it fresh rather than use unsaved form state.
  const handleReceiveClick = async () => {
    try {
      const response = await GetSupplyOrderByPoNumber(poNumberParam);
      setReceiveOrder(response?.results ?? response);
    } catch {
      NotificationManager.error("Error loading supply order");
    }
  };

  // Pull back only what receiving changed, so other unsaved edits on the page stay. Status must
  // refresh too, otherwise a later Update would send the old status and undo "Received Partially".
  const refreshAfterReceive = async () => {
    try {
      const response = await GetSupplyOrderByPoNumber(poNumberParam);
      const o = response?.results ?? response;
      if (!o) return;
      const savedItems = new Map((o.items || []).map((item) => [item.id, item]));
      setStatus(o.status || "Pending");
      setReceivedDate(o.receivedDate ?? null);
      setItems((current) =>
        current.map((item) =>
          savedItems.has(item.id)
            ? {
                ...item,
                quantityReceived: savedItems.get(item.id).quantityReceived,
                receiveLocationName: savedItems.get(item.id).receiveLocationName,
              }
            : item
        )
      );
      setHistory(o.history || []);
    } catch {
      NotificationManager.error("Error reloading supply order");
    }
  };

  // Only offer products from the vendor the order is placed with.
  const vendorProducts = vendorRecordId
    ? products.filter(
        (product) => product.vendorRecordId === parseInt(vendorRecordId, 10)
      )
    : products;

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: 0,
        productId: "",
        productVariantId: null,
        quantityOrdered: 1,
        quantityReceived: 0,
        orderLevel: "",
        unitCost: 0,
        receiveLocationName: "",
        notes: "",
      },
    ]);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };

    // Picking a product seeds the cost and order level from the product record.
    // Both are stored on the line so later price changes don't rewrite this order.
    if (field === "productId") {
      const product = products.find((item) => item.id === parseInt(value, 10));
      if (product) {
        updated[index].unitCost = product.cost ?? 0;
        updated[index].orderLevel = product.orderLevel || "";
      }
      updated[index].productVariantId = null;
    }

    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const lineTotal = (item) =>
    (Number(item.quantityOrdered) || 0) * (Number(item.unitCost) || 0);

  const itemsTotal = items.reduce((sum, item) => sum + lineTotal(item), 0);
  const orderTotal = itemsTotal + (Number(shippingCost) || 0);

  const buildPayload = () => ({
    poNumber: isEdit ? poNumberParam : null,
    vendorRecordId: parseInt(vendorRecordId, 10),
    status,
    priority,
    orderType: orderType || null,
    orderDate: orderDate || null,
    expectedDate: expectedDate || null,
    mustShipByDate: mustShipByDate || null,
    receivedDate,
    trackingNumber,
    carrier,
    vendorInvoiceNumber,
    shippingCost: Number(shippingCost) || 0,
    depositAmount: Number(depositAmount) || 0,
    amountPaid: Number(amountPaid) || 0,
    notes,
    items: items
      .filter((item) => item.productId)
      .map((item) => ({
        id: item.id || 0,
        poNumber: isEdit ? poNumberParam : null,
        productId: parseInt(item.productId, 10),
        productVariantId: item.productVariantId
          ? parseInt(item.productVariantId, 10)
          : null,
        quantityOrdered: Number(item.quantityOrdered) || 0,
        orderLevel: item.orderLevel,
        unitCost: Number(item.unitCost) || 0,
        receiveLocationName: item.receiveLocationName,
        notes: item.notes,
      })),
  });

  const handleSubmit = async () => {
    if (!vendorRecordId) {
      NotificationManager.error("Vendor is required");
      return;
    }

    if (!items.some((item) => item.productId)) {
      NotificationManager.error("Add at least one product to the order");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await UpdateSupplyOrder(buildPayload());
        NotificationManager.success("Supply order updated successfully");
        // Stay on the order and reload it; the search list lives in the other tab (ENB-605).
        fetchOrder();
        return;
      }
      // Save responds with the generated PO number.
      const response = await SaveSupplyOrder(buildPayload());
      NotificationManager.success(
        `Supply order ${response?.results ?? ""} created successfully`
      );
      navigate("/vlp/inventory-supply-orders");
    } catch {
      NotificationManager.error("Error saving supply order");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate("/vlp/inventory-supply-orders");
    }
  };

  const tf = (label, value, setter, extra = {}) => (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
      fullWidth
      {...extra}
    />
  );

  const select = (label, value, setter, options, extra = {}) => (
    <TextField
      size="small"
      select
      label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
      fullWidth
      {...extra}
    >
      {options}
    </TextField>
  );

  const actionButtons = (
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
      <Button variant="outlined" onClick={handleClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={saving}
      >
        {saving ? "Saving..." : isEdit ? "Update" : "Save"}
      </Button>
    </Box>
  );

  return (
    <div className="vlp-component-container">
      <span className="title">
        {isEdit ? (
          <>
            Edit Supply Order{" "}
            <Box component="span" sx={{ color: "primary.main" }}>
              {poNumberParam}
            </Box>
          </>
        ) : (
          "Add Supply Order"
        )}
      </span>
      <hr />

      <Paper elevation={2} sx={{ p: 2 }}>
        {!isEdit && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            The PO number is generated automatically when the order is saved.
          </Typography>
        )}
        <Grid container spacing={1.5}>
          {/* Row 1 */}
          <Grid item xs={2}>
            {select(
              "Vendor *",
              vendorRecordId,
              setVendorRecordId,
              vendors.map((vendor) => (
                <MenuItem key={vendor.recordId} value={vendor.recordId}>
                  {vendor.vendorName}
                </MenuItem>
              ))
            )}
          </Grid>
          <Grid item xs={2}>
            {select(
              "Status",
              status,
              setStatus,
              supplyOrderStatusList.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))
            )}
          </Grid>
          <Grid item xs={2}>
            {select(
              "Priority",
              priority,
              setPriority,
              supplyOrderPriorityList.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))
            )}
          </Grid>
          <Grid item xs={2}>
            <TextField
              size="small"
              label="Order Date"
              type="date"
              fullWidth
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              size="small"
              label="Expected Date"
              type="date"
              fullWidth
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={2}>
            {tf("Tracking Number", trackingNumber, setTrackingNumber, {
              inputProps: { maxLength: 100 },
            })}
          </Grid>
          <Grid item xs={2}>
            {tf("Carrier", carrier, setCarrier, {
              inputProps: { maxLength: 100 },
            })}
          </Grid>

          {/* Row 2 */}
          <Grid item xs={2}>
            {tf("Vendor Invoice #", vendorInvoiceNumber, setVendorInvoiceNumber, {
              inputProps: { maxLength: 100 },
            })}
          </Grid>
          <Grid item xs={2}>
            {tf("Shipping Cost", shippingCost, setShippingCost, {
              type: "number",
            })}
          </Grid>
          <Grid item xs={2}>
            {tf("Deposit Amount", depositAmount, setDepositAmount, {
              type: "number",
            })}
          </Grid>
          <Grid item xs={2}>
            {tf("Amount Paid", amountPaid, setAmountPaid, { type: "number" })}
          </Grid>
          <Grid item xs={2}>
            <TextField
              size="small"
              label="Order Total"
              fullWidth
              value={orderTotal.toFixed(2)}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={2}>
            {tf("Notes", notes, setNotes, { inputProps: { maxLength: 2000 } })}
          </Grid>
          <Grid item xs={2}>
            <TextField
              size="small"
              label="Must Ship By"
              type="date"
              fullWidth
              value={mustShipByDate}
              onChange={(e) => setMustShipByDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={2}>
            {select("Order Type", orderType, setOrderType, [
              <MenuItem key="" value="">
                <em>None</em>
              </MenuItem>,
              ...supplyOrderTypeList.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              )),
            ])}
          </Grid>
        </Grid>
      </Paper>

      {/* Order lines */}
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Typography variant="subtitle2">
            Order Lines (items total: ${itemsTotal.toFixed(2)})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            Add Line
          </Button>
        </div>
        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Variant</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Order Level</TableCell>
              <TableCell>Unit Cost</TableCell>
              <TableCell>Line Total</TableCell>
              <TableCell>Receive Into</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const product = products.find(
                (p) => p.id === parseInt(item.productId, 10)
              );

              return (
                <TableRow key={`item-${index}`}>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={item.productId || ""}
                      onChange={(e) =>
                        handleItemChange(index, "productId", e.target.value)
                      }
                      sx={{ minWidth: 150 }}
                    >
                      {vendorProducts.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.productName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={item.productVariantId || ""}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "productVariantId",
                          e.target.value
                        )
                      }
                      disabled={!product}
                      sx={{ minWidth: 110 }}
                    >
                      <MenuItem value="">(none)</MenuItem>
                      {(product?.variants || []).map((variant) => (
                        <MenuItem key={variant.id} value={variant.id}>
                          {variant.variantName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={item.quantityOrdered ?? 0}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "quantityOrdered",
                          e.target.value
                        )
                      }
                      // 80px clipped anything past 4 digits once the number spinner took its share.
                      sx={{ width: 130 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={item.orderLevel || ""}
                      onChange={(e) =>
                        handleItemChange(index, "orderLevel", e.target.value)
                      }
                      sx={{ minWidth: 90 }}
                    >
                      {inventoryOrderLevelList.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={item.unitCost ?? 0}
                      onChange={(e) =>
                        handleItemChange(index, "unitCost", e.target.value)
                      }
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>${lineTotal(item).toFixed(2)}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={item.receiveLocationName || ""}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "receiveLocationName",
                          e.target.value
                        )
                      }
                      sx={{ minWidth: 130 }}
                    >
                      <MenuItem value="">(choose later)</MenuItem>
                      {locationOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.notes || ""}
                      onChange={(e) =>
                        handleItemChange(index, "notes", e.target.value)
                      }
                      inputProps={{ maxLength: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      style={{ color: "red" }}
                      onClick={() => handleRemoveItem(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="textSecondary">
                    No lines yet. Pick the vendor first, then add the products
                    being ordered.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Attachments and history need a PO number, so they only show once the order exists.
          History sits on the left and the actions under attachments on the right, so a long
          history never pushes Update/Cancel down the page. */}
      {isEdit ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ flex: "1 1 500px" }}>
            <SupplyOrderHistoryTable history={history} onReceive={handleReceiveClick} />
          </Box>
          <Box sx={{ flex: "1 1 500px" }}>
            <SupplyOrderAttachmentsTable poNumber={poNumberParam} />
            {actionButtons}
          </Box>
        </Box>
      ) : (
        actionButtons
      )}

      <ReceiveSupplyOrderDialog
        open={Boolean(receiveOrder)}
        order={receiveOrder}
        onClose={() => setReceiveOrder(null)}
        onSave={refreshAfterReceive}
      />

      <CommonLoader loading={loading || saving} />
    </div>
  );
};

export default AddEditSupplyOrder;
